// https://github.com/kourge/ts-brand/blob/master/src/index.ts

import { nanoid } from "nanoid";
import invariant from "tiny-invariant";
import { z } from "zod";

export type Brand<Base, Branding, ReservedName extends string = "__type__"> = Base & {
  [K in ReservedName]: Branding;
} & { __witness__: Base };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyBrand = Brand<unknown, any>;

export type BaseOf<B extends AnyBrand> = B["__witness__"];

export type Brander<B extends AnyBrand> = (underlying: BaseOf<B>) => B;

export function identity<B extends AnyBrand>(underlying: BaseOf<B>): B {
  return underlying as B;
}

export type AssertionFunction<Input, Output extends Input> = (
  value: Input
) => asserts value is Output;

export function makeBrand<B extends AnyBrand>(
  validator?: AssertionFunction<BaseOf<B>, B>
): Brander<B> {
  if (!validator) {
    return identity;
  }

  return (underlying: BaseOf<B>): B => {
    (validator as Brander<B>)(underlying);
    return underlying as B;
  };
}

// --- custom stuff: ID related additions

const KIND_SYMBOL = Symbol("__kind__");
const DEFAULT_DELIMITER = "$";

export type BrandedId<Kind extends string> = Brand<string, Kind>;

export type BrandedIdConstructor<Kind extends string> = {
  (id?: string): BrandedId<Kind>;
  [KIND_SYMBOL]: Kind;

  validate: (id: string) => asserts id is BrandedId<Kind>;
  unwrap: (id: BrandedId<Kind>) => string;
};

export const makeBrandedId = <Kind extends string>(
  kind: Kind,
  delimiter = DEFAULT_DELIMITER
): BrandedIdConstructor<Kind> => {
  const prefix = `${kind}${delimiter}`;
  const brander = (id?: string) => {
    if (id?.startsWith(prefix)) {
      return id as BrandedId<Kind>;
    }

    return `${prefix}${id ?? nanoid()}` as BrandedId<Kind>;
  };

  brander[KIND_SYMBOL] = kind;

  brander.validate = (id: string) => {
    return typeof id === "string" && (id as string).startsWith(prefix);
  };

  brander.unwrap = (id: BrandedId<Kind>) => {
    invariant(brander.validate(id), `Cannot unwrap non-branded id: ${id}, prefix: ${prefix}`);

    return id.slice(prefix.length);
  };

  return brander;
};

export type BrandedIdSchema<Kind extends string> = z.ZodType<BrandedId<Kind>> & {
  brander: BrandedIdConstructor<Kind>;
};

export const makeBrandedIdSchema = <Kind extends string>(
  brander: BrandedIdConstructor<Kind>
): BrandedIdSchema<Kind> => {
  const schema = z.string().refine((value) => brander.validate(value), {
    message: `Invalid branded ID format`,
  });

  return Object.assign(schema, { brander }) as unknown as BrandedIdSchema<Kind>;
};
